import { useCallback, useEffect, useState } from "react";
import { Building2, Link2, Save, UserRoundX } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function MappingRow({ row, candidates, canMap, onSave, onClear }) {
  const current = row.mapping?.provider_id || (row.mapping?.ignored ? "__ignored__" : "");
  const [value, setValue] = useState(current || row.suggested_provider_id || "");
  useEffect(() => setValue(current || row.suggested_provider_id || ""), [current, row.suggested_provider_id]);
  const dirty = value !== current;
  const suggested = !current && row.suggested_provider_id && candidates.find((c) => c.id === row.suggested_provider_id);
  return (
    <tr data-testid={`mapping-row-${row.connecteam_user_id}`} className={row.unmatched ? "mapping-unmatched" : ""}>
      <td><strong>{row.first_name} {row.last_name}</strong>{row.is_archived && <span className="status-chip status-inactive"> archived</span>}<br /><small>{row.title || "—"}{row.sites?.length ? ` · ${row.sites.join(", ")}` : ""}</small></td>
      <td>{row.email || <span className="pairing-meta">no email</span>}</td>
      <td data-testid={`mapping-current-${row.connecteam_user_id}`}>
        {row.mapping?.ignored ? <span className="pairing-meta">Ignored (not tracked)</span> : row.mapped_name ? <>{row.mapped_name}{!row.mapped_active && <small className="flag-text"> · inactive in TAGForce</small>}<br /><small>{row.mapping.method === "manual" ? `by ${row.mapping.mapped_by_name}` : `auto · ${row.mapping.method}`}</small></> : <><span className="flag-text" data-testid={`mapping-unmatched-${row.connecteam_user_id}`}>Unmatched</span>{suggested && <><br /><small data-testid={`mapping-suggested-${row.connecteam_user_id}`}>Suggested: {suggested.name} (similar name — confirm before saving)</small></>}</>}
      </td>
      <td>
        {canMap ? (
          <div className="mapping-actions">
            <select data-testid={`mapping-select-${row.connecteam_user_id}`} value={value} onChange={(e) => setValue(e.target.value)}>
              <option value="">— Not mapped —</option>
              <option value="__ignored__">Ignore (not a TAGForce person)</option>
              <optgroup label="Providers">{candidates.filter((c) => c.is_provider).map((c) => <option key={c.id} value={c.id}>{c.name}{c.discipline ? ` · ${c.discipline}` : ""}</option>)}</optgroup>
              <optgroup label="Staff">{candidates.filter((c) => !c.is_provider).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.role}</option>)}</optgroup>
            </select>
            <button className="button button-secondary" data-testid={`mapping-save-${row.connecteam_user_id}`} disabled={!dirty} type="button" onClick={() => (value ? onSave(row, value) : onClear(row))}><Save size={13} /> Save</button>
          </div>
        ) : <span className="pairing-meta">View only</span>}
      </td>
    </tr>
  );
}

function SiteRow({ job, clients, canMap, onSave }) {
  const current = job.mapping?.client_id || (job.mapping?.ignored ? "__ignored__" : "");
  const [value, setValue] = useState(current);
  useEffect(() => setValue(current), [current]);
  return (
    <tr data-testid={`site-row-${job.job_id}`}>
      <td><strong>{job.title}</strong><br /><small>{job.code || "no code"}{job.latitude != null ? " · GPS on file" : ""}</small></td>
      <td data-testid={`site-current-${job.job_id}`}>{job.mapping?.ignored ? <span className="pairing-meta">Ignored (not a client site)</span> : job.mapping?.client_name || <span className="flag-text">Unmapped</span>}</td>
      <td>
        {canMap ? (
          <div className="mapping-actions">
            <select data-testid={`site-select-${job.job_id}`} value={value} onChange={(e) => setValue(e.target.value)}>
              <option value="">— Not mapped —</option>
              <option value="__ignored__">Ignore (internal / not a client site)</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.status === "Inactive" ? " (inactive)" : ""}</option>)}
            </select>
            <button className="button button-secondary" data-testid={`site-save-${job.job_id}`} disabled={value === current} type="button" onClick={() => onSave(job, value)}><Save size={13} /> Save</button>
          </div>
        ) : <span className="pairing-meta">View only</span>}
      </td>
    </tr>
  );
}

export function UserMappingPanel({ canMap, onChanged }) {
  const [data, setData] = useState(null);
  const [sites, setSites] = useState(null);
  const [onlyUnmatched, setOnlyUnmatched] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [mapping, jobs] = await Promise.all([api.get("/connecteam/mapping"), api.get("/connecteam/jobs")]);
      setData(mapping.data);
      setSites(jobs.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function saveUser(row, value) {
    try {
      await api.put(`/connecteam/mapping/${row.connecteam_user_id}`, value === "__ignored__" ? { ignored: true } : { provider_id: value });
      setNotice(`${row.first_name} ${row.last_name} ${value === "__ignored__" ? "marked as not tracked" : "mapped"}. Weekly hours and exceptions were recomputed.`);
      setError("");
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function clearUser(row) {
    try {
      await api.delete(`/connecteam/mapping/${row.connecteam_user_id}`);
      setNotice(`Mapping removed for ${row.first_name} ${row.last_name}.`);
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function saveSite(job, value) {
    try {
      await api.put(`/connecteam/jobs/${job.job_id}`, value === "__ignored__" ? { ignored: true } : { client_id: value || null });
      setNotice(`Site “${job.title}” ${value ? "mapped" : "unmapped"}.`);
      setError("");
      await load();
      onChanged?.();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  if (!data) return error ? <p className="form-message error-message" data-testid="mapping-error">{error}</p> : <p className="projection-loading">Loading Connecteam users…</p>;
  const rows = data.users.filter((row) => !onlyUnmatched || row.unmatched);
  return (
    <section className="connecteam-panel" data-testid="connecteam-mapping">
      <div className="section-heading">
        <div>
          <h2><Link2 size={16} /> Connecteam users → TAGForce people</h2>
          <p>Matched automatically by email, then by name. Map the rest here; the mapping drives weekly hours, roster reconciliation, time exceptions and supervision FTE.</p>
        </div>
        <label className="checkbox-row"><input checked={onlyUnmatched} data-testid="mapping-only-unmatched" type="checkbox" onChange={(e) => setOnlyUnmatched(e.target.checked)} /> Show unmatched only (<span data-testid="mapping-unmatched-count">{data.unmatched}</span>)</label>
      </div>
      {error && <p className="form-message error-message" data-testid="mapping-error">{error}</p>}
      {notice && <p className="form-message success-message" data-testid="mapping-notice">{notice}</p>}
      <div className="requests-table-panel">
        <table className="requests-table connecteam-table" data-testid="mapping-table">
          <thead><tr><th>Connecteam user</th><th>Email</th><th>TAGForce person</th><th>Mapping</th></tr></thead>
          <tbody>
            {rows.map((row) => <MappingRow key={row.connecteam_user_id} row={row} candidates={data.candidates} canMap={canMap} onSave={saveUser} onClear={clearUser} />)}
            {!rows.length && <tr><td colSpan={4} className="provider-empty-state" data-testid="mapping-empty"><UserRoundX size={14} /> {onlyUnmatched ? "Every active Connecteam user is matched or ignored." : "No Connecteam users synced yet."}</td></tr>}
          </tbody>
        </table>
      </div>
      {sites && (
        <details className="supervision-history" data-testid="site-mapping" open={sites.jobs.some((j) => !j.mapping)}>
          <summary><Building2 size={13} /> Connecteam sites (jobs) → TAGForce clients · {sites.jobs.filter((j) => !j.mapping).length} unmapped</summary>
          <div className="requests-table-panel">
            <table className="requests-table connecteam-table" data-testid="site-table">
              <thead><tr><th>Connecteam site</th><th>TAGForce client</th><th>Mapping</th></tr></thead>
              <tbody>{sites.jobs.map((job) => <SiteRow key={job.job_id} job={job} clients={sites.clients} canMap={canMap} onSave={saveSite} />)}</tbody>
            </table>
          </div>
        </details>
      )}
    </section>
  );
}
